/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import type { CSSProperties, ReactNode } from "react";

import { useMemo } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { App as AntdApp, ConfigProvider } from "antd";
import resolveConfig from "tailwindcss/resolveConfig";

import type { UiOptionActions } from "./useUiOptions";

import tailwindConfig from "../../../../tailwind.config";
import { DesignProvider } from "./designProvider";
import { useBodyBgColorUpdater } from "./useBodyBgColorUpdater";
import {
  Reset,
  SetBorderRadius,
  SetDarkMode,
  SetFontSize,
  SetShowMenu,
  useUiOptions,
} from "./useUiOptions";

export interface DesignSystemProviderProps {
  children?: ReactNode;
}

interface AgGridThemeVariables extends CSSProperties {
  "--ag-cell-horizontal-padding": string;
  "--ag-header-height": string;
  "--ag-alpine-active-color": string;
  "--ag-background-color": string;
  "--ag-odd-row-background-color": string;
  "--ag-row-hover-color": string;
  "--ag-selected-row-background-color": string;
  "--ag-header-background-color": string;
  "--ag-border-color": string;
  "--ag-value-change-value-highlight-background-color": string;
  "--alaffia-ag-border-radius": string;
  "--alaffia-ag-header-icon-color": string;
  "--alaffia-ag-sort-icon-color": string;
}

/**
 * Provides theme management for antd and tailwind -- using the same colors
 * See the tailwind.readme.md in the root for details about how the colors are
 * loaded in the tailwind.config.js; they are read using the tailwind config
 * resolver below, and provided back to the antd config provider to control its theme.
 * @param children
 * @constructor
 */
export const DesignSystemProvider = ({
  children,
}: DesignSystemProviderProps) => {
  const twConfig = useMemo(() => resolveConfig(tailwindConfig), []);

  const [uiOptions, _updateUiOption] = useUiOptions();

  const updateUiOption = (opt: UiOptionActions) => {
    _updateUiOption(opt);
  };

  const designProvider = useMemo(
    () => ({
      options: uiOptions,
      toggleDarkMode: () =>
        updateUiOption(new SetDarkMode(!uiOptions.darkMode)),
      setDarkMode: (enabled: boolean) =>
        updateUiOption(new SetDarkMode(enabled)),
      setBorderRadius: (borderRadius: number) =>
        updateUiOption(new SetBorderRadius(borderRadius)),
      setFontSize: (fontSize: number) =>
        updateUiOption(new SetFontSize(fontSize)),
      reset: () => updateUiOption(new Reset()),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uiOptions],
  );

  const antdTokens = useMemo(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    () => twConfig?.theme?.antd || { light: {}, dark: {} },
    [twConfig],
  );

  const agGridThemeVariables = useMemo<AgGridThemeVariables>(() => {
    const theme = uiOptions.darkMode ? "dark" : "light";
    const tokens = antdTokens[theme];

    return {
      "--ag-cell-horizontal-padding": "0.75rem",
      "--ag-header-height": "36px",
      "--ag-alpine-active-color": tokens.colorPrimaryBorder,
      "--ag-background-color": tokens.colorBgContainer,
      "--ag-odd-row-background-color": tokens.colorBgElevated,
      "--ag-row-hover-color": tokens.colorPrimaryBg,
      "--ag-selected-row-background-color": tokens.colorInfoBg,
      "--ag-header-background-color": tokens.bgColor,
      "--ag-border-color": tokens.colorBorderSecondary,
      "--ag-value-change-value-highlight-background-color": tokens.colorPrimary,
      "--alaffia-ag-border-radius": `${antdTokens.light.borderRadius}px`,
      "--alaffia-ag-header-icon-color": tokens.colorHighlight,
      "--alaffia-ag-sort-icon-color": tokens.colorWarningText,
    };
  }, [antdTokens, uiOptions.darkMode]);

  const appStyle = useMemo<CSSProperties>(
    () => ({
      width: "100%",
      height: "100%",
      ...agGridThemeVariables,
    }),
    [agGridThemeVariables],
  );

  useHotkeys("ctrl+shift+alt+m", () =>
    updateUiOption(new SetDarkMode(!uiOptions.darkMode)),
  );
  useHotkeys("ctrl+shift+meta+m", () =>
    updateUiOption(new SetShowMenu(!uiOptions.showMenu)),
  );

  useBodyBgColorUpdater(uiOptions.darkMode);

  return (
    <DesignProvider value={designProvider}>
      <ConfigProvider
        theme={{
          token: {
            ...(uiOptions.darkMode ? antdTokens.dark : antdTokens.light),
            fontSizeHeading3: 18,
            ...(uiOptions.fontSize &&
            uiOptions.fontSize >= 5 &&
            uiOptions.fontSize !== 14
              ? ((f: number) => ({
                  fontSize: f,
                  fontSizeSM: f - 2,
                  fontSizeLG: f + 2,
                  fontSizeXL: f > 14 ? f + 4 : f + 6,
                  fontSizeHeading1: f * 2 + 8,
                  fontSizeHeading2: f * 2,
                  fontSizeHeading3: f + 8,
                  fontSizeHeading4: f + 4,
                  fontSizeHeading5: f + 2,
                }))(uiOptions.fontSize)
              : {}),
            ...(uiOptions.borderRadius === 2
              ? {
                  borderRadius: 2,
                  borderRadiusXS: 1,
                  borderRadiusSM: 2,
                  borderRadiusLG: 3,
                  borderRadiusOuter: 1,
                }
              : {}),
          },
        }}
      >
        <AntdApp style={appStyle}>{children}</AntdApp>
      </ConfigProvider>
    </DesignProvider>
  );
};
